import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["upgradeData", "offerProductConfigurations", "nonRecurringProductConfigurations", "currencyCode", "productBasedOffers", "onClick", "onError", "children", "beforeRedirect", "products", "shouldShowErrorAlert", "disabled", "redirectQueryParams", "use", "data-test-id"];
/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFloatingAlertList from 'UIComponents/alert/UIFloatingAlertList';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import { useEffect, useState } from 'react';
import { checkout } from 'self-service-api/core/utilities/links';
import styled from 'styled-components';
import * as tracker from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import UIContactSalesButton from 'ui-addon-upgrades/button/UIContactSalesButton';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { omit } from '../../utils';
import { getProducts } from '../common/api/getProducts';
import AdminOnlyWrapper from '../common/components/AdminOnlyWrapper';
import { REQUEST_SKU } from '../common/constants/AdminModalTypes';
import { BILLING } from '../common/constants/AdminTypes';
import { navigateTo } from '../common/navigation/url';
import logPurchaseOrderError from '../common/reliability/logCheckoutError';
import logError from '../common/reliability/logError';
import { BUY_TOUCHLESS } from '../purchaseMotions/PurchaseMotionTypes';
import { incrementPurchaseOrderGenerationFailure, incrementPurchaseOrderGenerationSuccess, trackPurchaseMotionFailure } from '../utils/metricUtils';
import { getIsMultipleOfferQuantityUpgrade, getProductTrackingName } from '../utils/productUtils';
import CheckoutFloatingAlertStore from './CheckoutFloatingAlertStore';
import { generateMultipleOfferPurchaseOrder } from './generatePurchaseOrder';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledContactSalesButton = styled(UIContactSalesButton).withConfig({
  displayName: "MultipleOfferCheckoutButton__StyledContactSalesButton",
  componentId: "sc-1g4bhel-0"
})(["display:none !important;"]);

// `checkout` receives a second parameter, upgradeSource, which will
// append the upgradeSource as a query parameter. In order to pass in additional
// query parameters without including an extra `?`, we'll append all query params
// in one operation
export const createCheckoutUrlWithQueryParameters = ({
  purchaseOrderId,
  redirectUrlQueryParams = {}
}) => {
  const checkoutUrl = new URL(checkout(purchaseOrderId));
  for (const [queryParamKey, queryParamValue] of Object.entries(redirectUrlQueryParams)) {
    checkoutUrl.searchParams.append(queryParamKey, queryParamValue.toString());
  }
  return checkoutUrl.href;
};
const MultipleOfferCheckoutButton = _ref => {
  let {
      upgradeData,
      offerProductConfigurations = [],
      nonRecurringProductConfigurations = [],
      currencyCode,
      productBasedOffers = [],
      onClick = () => {},
      onError = () => {},
      children,
      beforeRedirect = () => Promise.resolve(true),
      products = null,
      shouldShowErrorAlert = false,
      disabled = false,
      redirectQueryParams,
      use = 'primary',
      'data-test-id': dataTestId
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [productList, setProductList] = useState(products);
  const [lastOrderGenerationFailed, setLastOrderGenerationFailed] = useState(false);
  const [isGeneratingPurchaseOrder, setIsGeneratingPurchaseOrder] = useState(false);
  const [isFetchingProducts, setIsFetchingProducts] = useState(!productList);
  const [hasError, setHasError] = useState(false);
  const [trackingProperties, setTrackingProperties] = useState({});
  const allProductConfigurations = [...offerProductConfigurations.map(({
    productConfigurations
  }) => productConfigurations).flat(), ...nonRecurringProductConfigurations];
  const skuIds = allProductConfigurations.map(({
    skuId
  }) => skuId);
  const offerIds = offerProductConfigurations.map(({
    offerId
  }) => offerId).flat();
  const getCheckoutButtonInteractionTrackingProperties = () => {
    const productNames = skuIds.map(skuId => {
      return getProductTrackingName(productList, skuId);
    });
    const isQuantityUpgrade = getIsMultipleOfferQuantityUpgrade(productList, offerProductConfigurations);
    return {
      currencyCode,
      productBasedOfferIds: productBasedOffers,
      isQuantityUpgrade,
      checkoutType: 'full',
      skuIds,
      productNames,
      offerIds
    };
  };
  useEffect(() => {
    if (productList && (offerProductConfigurations.length || nonRecurringProductConfigurations.length)) {
      setTrackingProperties(Object.assign({}, getUpgradeDataTrackingProperties(upgradeData), getCheckoutButtonInteractionTrackingProperties()));
      tracker.track('checkoutButtonInteraction', Object.assign({
        action: 'viewed'
      }, getUpgradeDataTrackingProperties(upgradeData), getCheckoutButtonInteractionTrackingProperties()));
    } else {
      setIsFetchingProducts(true);
      getProducts().then(fetchedProducts => {
        setIsFetchingProducts(false);
        setProductList(fetchedProducts);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productList, offerProductConfigurations, nonRecurringProductConfigurations]);
  const onOrderGenerationStart = e => {
    setLastOrderGenerationFailed(false);
    setIsGeneratingPurchaseOrder(true);
    tracker.track('checkoutButtonInteraction', Object.assign({
      action: 'clicked'
    }, trackingProperties));
    if (onClick) {
      onClick(e);
    }
  };
  const onOrderGenerationComplete = () => {
    setLastOrderGenerationFailed(false);
    setIsGeneratingPurchaseOrder(false);
    tracker.track('checkoutButtonInteraction', Object.assign({
      action: 'purchase order generated'
    }, trackingProperties));
    incrementPurchaseOrderGenerationSuccess({
      type: 'full'
    });
  };
  const onOrderGenerationFailed = err => {
    if (onError) {
      onError(err);
    }
    trackPurchaseMotionFailure({
      uniqueId: upgradeData.uniqueId,
      apiName: upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct],
      purchaseMotion: BUY_TOUCHLESS
    });
    setLastOrderGenerationFailed(true);
    setIsGeneratingPurchaseOrder(false);
    tracker.track('checkoutButtonInteraction', Object.assign({
      action: 'purchase order generation failed'
    }, trackingProperties));
    logError('generatePurchaseOrder', err, Object.assign({}, trackingProperties));
    logPurchaseOrderError('generatePurchaseOrder', err, Object.assign({
      buttonName: 'MultipleOfferCheckoutButton'
    }, trackingProperties));
    incrementPurchaseOrderGenerationFailure({
      type: 'full'
    });
    if (shouldShowErrorAlert) {
      setHasError(true);
      CheckoutFloatingAlertStore.addAlert({
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.buyNowFailureAlert.title"
        }),
        message: /*#__PURE__*/_jsxs(UIButtonWrapper, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgrades.buyNowFailureAlert.message"
          }), /*#__PURE__*/_jsx(UIButton, {
            onClick: () => {
              CheckoutFloatingAlertStore.resetStore();
              document.getElementById('hidden-contact-sales-button').click();
            },
            use: "tertiary",
            size: "extra-small",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "upgrades.contactSales"
            })
          })]
        }),
        type: 'danger'
      });
    }
  };
  const handleClick = e => {
    if (!productList) return;
    onOrderGenerationStart(e);
    generateMultipleOfferPurchaseOrder(currencyCode, offerProductConfigurations, nonRecurringProductConfigurations, productList, productBasedOffers).then(orderId => {
      onOrderGenerationComplete();
      const queryParams = Object.assign({}, redirectQueryParams);
      if (upgradeData.upgradeSource) {
        queryParams.upgradeSource = upgradeData.upgradeSource;
      }
      const checkoutUrl = createCheckoutUrlWithQueryParameters({
        purchaseOrderId: orderId,
        redirectUrlQueryParams: queryParams
      });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      beforeRedirect(checkoutUrl).then((shouldRedirect = true) => shouldRedirect && navigateTo(checkoutUrl));
    }).catch(onOrderGenerationFailed);
  };
  const propsToPass = omit(rest, ['allowModal', 'shouldShowErrorAlert', 'products', 'isUpgradePoint', 'level', 'trackingSource', 'data-selenium-id', 'canViewNewSeats', 'requestedProducts', 'terms']);
  const originalId = rest['data-selenium-id'];
  const idToUse = isFetchingProducts ? `${originalId}-disabled` : originalId;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [hasError && shouldShowErrorAlert && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(StyledContactSalesButton, {
        upgradeData: Object.assign({}, upgradeData, {
          uniqueId: 'checkout-error'
        }),
        id: "hidden-contact-sales-button"
      }), /*#__PURE__*/_jsx(UIFloatingAlertList, {
        alertStore: CheckoutFloatingAlertStore
      })]
    }), /*#__PURE__*/_jsx(AdminOnlyWrapper, {
      upgradeData: upgradeData,
      adminType: BILLING,
      modalType: REQUEST_SKU,
      children: /*#__PURE__*/_jsx(UILoadingButton, Object.assign({}, propsToPass, {
        failed: lastOrderGenerationFailed,
        loading: isGeneratingPurchaseOrder,
        disabled: isFetchingProducts || disabled,
        onClick: handleClick,
        "data-selenium": "checkout-button",
        "data-selenium-id": idToUse,
        "data-test-id": dataTestId || 'buy-now-button',
        use: use,
        children: children || /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'upgrades.checkoutButton.default'
        })
      }))
    })]
  });
};
export default MultipleOfferCheckoutButton;